/**
 * Small confirm dialog on the shared Modal — a title, an optional detail line,
 * and big glove-friendly Cancel / confirm buttons. `tone` colours the confirm
 * button: `primary` (green) for positive actions like adding, `danger` (red)
 * for destructive ones like removing.
 */
import type { ReactNode } from 'react'
import { Modal } from '@vollgas/shared/ui'
import { cn } from '@vollgas/shared/utils'
import { useI18n } from '@/i18n/TranslationContext'

interface Props {
  readonly title: string
  readonly detail?: ReactNode
  readonly confirmLabel: string
  readonly cancelLabel?: string
  readonly tone?: 'primary' | 'danger'
  readonly onConfirm: () => void
  readonly onClose: () => void
}

export function ConfirmDialog({ title, detail, confirmLabel, cancelLabel, tone = 'primary', onConfirm, onClose }: Props) {
  const { t } = useI18n()
  const cancel = cancelLabel ?? t.common.cancel
  return (
    <Modal onClose={onClose} closeLabel={t.common.close} size="sm">
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-15 font-extrabold text-glass-text">{title}</h2>
          {detail && <div className="mt-1 text-13 text-glass-text-muted">{detail}</div>}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-glass-border bg-transparent px-5 py-2.5 text-13 font-bold text-glass-text transition hover:bg-glass-fill-strong"
          >
            {cancel}
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm()
              onClose()
            }}
            className={cn(
              'rounded-full px-5 py-2.5 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97]',
              tone === 'danger' ? 'bg-accent' : 'bg-green',
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  )
}
